/**
 * Created by mnaughton on 10/16/2015.
 */

import ButtonEditor from "core/views/Field/edit/ButtonEditor";
import CheckBoxEditor from "core/views/Field/edit/CheckBoxView";
import ColorEditor from "core/views/Field/edit/ColorEditor";
import DateEditor from "core/views/Field/edit/DateEditor";
import FileEditor from "core/views/Field/edit/FileEditor";
import InputView from "core/views/Field/edit/InputView";
import RadioButtonView from "core/views/Field/edit/RadioButtonView";
import SelectView from "core/views/Field/edit/SelectView";
import TextAreaView from "core/views/Field/edit/TextAreaView";
import CheckBoxViewer from "core/views/Field/view/CheckBoxView";
import FileView from "core/views/Field/view/FileView";
import ListView from "core/views/Field/view/ListView";
import RawView from "core/views/Field/view/RawView";
import Backbone from "backbone";
import Field from "core/models/Field";
import _ from "underscore";
import s from "underscore.string";
import ImageView from "core/views/Field/view/ImageView";
import FormattedView from "core/views/Field/view/FormattedView";
import LinkView from "core/views/Field/view/LinkView";
import BaseFieldView from "core/views/Field/BaseFieldView";
import EmailView from "core/views/Field/view/EmailView";
import FromNowView from "core/views/Field/view/FromNowView";
import CheckListEditor from "core/views/Field/edit/CheckListEditor";
import CheckListView from "core/views/Field/view/CheckListView";

/**
 * Creates a new FieldViewFactory.
 * @constructor
 */
function FieldViewFactory() {}

FieldViewFactory.prototype = {
  /**
   * Normalizes the field parameter. If the `field` is a plain object, a `Field` will be created, passing the `field` as options. If it is a string and a `Model` was supplied to this `FieldViewFactory` constructor with a matching field, an instance of `Field` initialized by that field will be created. If `field` is a string but no matching field is found, or there was no input Model, then an editable text field is created using the `field` parameter as `name` and `label`.
   * @param {Field|Object|String} field The field parameter.
   * @return {Field} A normalized Field object.
   * @private
   */
  _normalizeField: function (field) {
    var result = field;

    if (_.isString(field)) {
      result = {
        name: field,
        label: s.titleize(field),
        editor: "text",
      };
    } else {
      result = field;
    }

    if (_.isObject(result) && !(result instanceof Field)) {
      result = new Field(result);
    }

    return result;
  },

  /**
   * Normalizes the `model` parameter. If the model is not an instance of `Backbone.Model`, a `Backbone.Model` is constructed using the `model` parameter as attributes. If a `Model` option was supplied, that constructor will be used instead.
   * @param {Object|Backbone.Model} model The model parameter.
   * @return {Backbone.Model} A normalized model object.
   * @private
   */
  _normalizeModel: function (model) {
    var result = model;

    if (!(model instanceof Backbone.Model)) {
      var Model = Backbone.Model;

      result = new Model(model);
    }

    return result;
  },

  /**
   * Normalizes the `options` parameter.
   * @param {Backbone.Model} model A normalized model object.
   * @param {Field} field A normalized field object.
   * @param {Object} [options] An options hash.
   * @return {Object} An options hash with values for `context`, `field`, `model`, and `value`, in addition to any options supplied in the `options` parameter.
   * @private
   */
  _normalizeOptions: function (model, field, options) {
    return _.extend(
      {
        field: field,
        value: field.getValue(model),
        model: model,
      },
      field.get("viewOptions"),
      options
    );
  },

  /**
   * Wires up the view and model to synchronize with each other.
   * @param {Field} field A normalized field object.
   * @param {Backbone.Model} model A normalized model object.
   * @param {Backbone.View} view A view object.
   * @return {Backbone.View} The wired view object.
   * @private
   */
  _wire: function (field, model, view) {
    var fieldName = field.get("editValue") || field.get("name"); //todo figure out how to handle view value and edit value
    var changing = false;

    model.on("change:" + fieldName, function () {
      if (!changing) {
        view.value = model.get(fieldName);
      }
    });

    view.on("change", function (value) {
      changing = true;
      field.setValue(model, value);
      changing = false;
    });

    return view;
  },

  /**
   * Get an editor appropriate for the given field on the given model. If the field is of type "custom", and is editable, it is *recommended* to provide an `EditorView` attribute, containing an object which will be used as the view to render after being passed through `BaseFieldView.extend`.
   * @param {Field|Object|String} field A field instance, attributes with which to initialize a field instance, or the identifier of a field instance.
   * @param {Object|Backbone.Model} model A model instance.
   * @param {Object} [options] An options hash.
   * @param {String} [options.type] Used to override the value in `field.editor`, which influences the type of view generated.
   * @return {Backbone.View} A view which displays the model field information and is wired up to handle changes.
   */
  getEditor: function (field, model, options) {
    var EditorView;

    field = this._normalizeField(field);
    model = this._normalizeModel(model);
    options = this._normalizeOptions(model, field, options);

    if (!field.isEditable(model)) {
      return this.getViewer(field, model, options);
    }

    switch (options.type || field.get("editor")) {
      case "textarea":
        EditorView = TextAreaView;
        break;

      case "checkbox":
        EditorView = CheckBoxEditor;
        break;

      case "check":
        EditorView = CheckListEditor;
        break;

      case "select":
        EditorView = SelectView;
        break;

      case "radio":
        EditorView = RadioButtonView;
        break;

      case "color":
        EditorView = ColorEditor;
        break;

      case "image":
      case "file":
        EditorView = FileEditor;
        break;

      case "fromnow":
      case "date":
        EditorView = DateEditor;
        break;

      case "button":
        EditorView = ButtonEditor;
        break;

      case "custom":
        if (field.get("EditorView")) {
          EditorView = BaseFieldView.extend(field.get("EditorView"));
        } else {
          EditorView = InputView; //could we break here?
        }
        break;

      default:
        EditorView = InputView;
        break;
    }

    return this._wire(field, model, new EditorView(options));
  },

  /**
   * Get a viewer appropriate for the given field on the given model. If the field is of type "custom", it is *recommended* to provide a `ViewerView` attribute, containing an object which will be used as the view to render after being passed through `BaseFieldView.extend`.
   * @param {Field|Object|String} field A field instance, attributes with which to initialize a field instance, or the identifier of a field instance.
   * @param {Object|Backbone.Model} model A model instance.
   * @param {Object} [options] An options hash.
   * @param {String} [options.type] Used to override the value in `field.editor`, which influences the type of view generated.
   * @return {Backbone.View} A view which displays the model field information and is wired up to handle changes.
   */
  getViewer: function (field, model, options) {
    var ViewerView;

    field = this._normalizeField(field);
    model = this._normalizeModel(model);
    options = this._normalizeOptions(model, field, options);

    switch (options.type || field.get("editor")) {
      case "checkbox":
        ViewerView = CheckBoxViewer;
        break;

      case "check":
        ViewerView = CheckListView;
        break;

      case "file":
        ViewerView = FileView;
        break;

      case "image":
        ViewerView = ImageView;
        break;

      case "phone":
        ViewerView = FormattedView;
        options.regex = "(\\d\\d\\d)(\\d\\d\\d)(\\d\\d\\d\\d)";
        options.format = "({0}) {1}-{2}";
        break;

      case "formatted":
        ViewerView = FormattedView;
        break;

      case "link":
        ViewerView = LinkView;
        break;

      case "email":
        ViewerView = EmailView;
        break;

      case "fromnow":
        ViewerView = FromNowView;
        break;

      case "date":
        var dateString = Date.parse(options.value);

        //Invalid date will return NaN for Date.parse. NaN will evaluate to false when compared against itself.
        if (dateString === dateString) {
          options.value = new Date(options.value).toLocaleDateString();
        }
        ViewerView = RawView;
        break;
      case "select":
      case "radio":
      case "button":
        ViewerView = ListView;
        break;

      case "custom":
        if (field.get("ViewerView")) {
          ViewerView = BaseFieldView.extend(field.get("ViewerView"));
        } else {
          ViewerView = RawView;
        }
        break;

      default:
        ViewerView = RawView;
        break;
    }

    return this._wire(field, model, new ViewerView(options));
  },
};

export default new FieldViewFactory();



//////////////////
// WEBPACK FOOTER
// ./public/js/core/views/Field/FieldViewFactory.js
// module id = 167
// module chunks = 1